import React, { useEffect, useRef, useState } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { AgGridReact } from "ag-grid-react";

import DupontButton from "../DupontButton";

import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";

import "./index.scss";

const AgTable = ({
  gridRef,
  rowData,
  columnDefs,
  quickFilterText,
  context,
  onRowClicked,
  onCellClicked,
  customHeight,
  frameworkComponents,
  editType,
  getRowNodeId,
  onSelectionChanged,
  rowHeight,
  onGridReady,
  suppressRowClickSelection,
  rowSelection,
  onRowSelected,
  setBadgeCount,
  suppressScrollOnNewData,
  customTableClass,
  hideExport,
  dynamicTextConditions,
  name,
  onCellValueChanged,
  onSortChanged,
  infolabel,
  getRowClass,
}) => {
  const { t } = useTranslation();
  customHeight = customHeight
    ? customHeight
    : rowData?.length > 7
      ? "400px"
      : "auto";

  rowHeight = rowHeight || 37;
  suppressRowClickSelection = suppressRowClickSelection || true;
  rowSelection = rowSelection || "multiple";
  customTableClass = customTableClass || "ag-theme-custom-react";

  const [columns, setColumns] = useState([]);
  const [colState, setColumnState] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const agGridRef = useRef();

  const getGridAPI = () => {
    const ref = gridRef || agGridRef;
    return ref?.current?.api;
  };

  useEffect(() => {
    setColumns(columnDefs);
  }, [columnDefs]);

  useEffect(() => {
    if (columnApi && colState) {
      columnApi.applyColumnState(colState);
    }
  }, [rowData]);

  const onAgGridReady = (params) => {
    setColumnApi(params.columnApi);
    if (onGridReady) {
      params.api.sizeColumnsToFit();
      onGridReady(params);
    }
  };

  // const onSortChanged = (par) => {
  //   setColumnState(par.columnApi.getColumnState());
  // };

  const defaultColDef = {
    editable: false,
    sortable: true,
    unSortIcon: true,
    resizable: true,
    suppressSizeToFit: false,
    sortingOrder: ["asc", "desc"],
    filter: false,
    filterParams: {
      buttons: ["apply", "reset"],
      closeOnApply: true,
      newRowsAction: "keep",
    },
    suppressMenu: rowData.length === 0,
    flex: 1,
  };
  const onFilterChanged = (event) => {
    const resultsCount = event.api.getDisplayedRowCount();
    if (setBadgeCount) setBadgeCount(resultsCount.toString());
    const ref = getGridAPI();
    if (ref) {
      if (resultsCount === 0) {
        ref.showNoRowsOverlay();
      } else {
        ref.hideOverlay();
      }
    }
  };

  const gridOptions = {
    rowHeight,
    editType,
    rowSelection,
    suppressRowClickSelection,
    suppressMultiSort: true,
    overlayNoRowsTemplate: `<span id="aggrid-no-data-msg">${t(
      "ValueStrings.noRows",
    )}</span>`,
  };

  const excludeColumnsRecursively = (columns, excludedColIds) => {
    return columns.reduce((acc, column) => {
      if (column.children) {
        const updatedChildren = excludeColumnsRecursively(
          column.children,
          excludedColIds,
        );

        if (updatedChildren.length === column.children.length) {
          if (!excludedColIds.includes(column.colId)) {
            excludedColIds.push(column.colId);
          }
        } else {
          acc.push({ ...column, children: updatedChildren });
        }
      } else if (!excludedColIds.includes(column.colId)) {
        acc.push(column);
      }

      return acc;
    }, []);
  };

  const onExportClicked = useCallback(() => {
    const ref = getGridAPI();
    const columns = ref.getColumnDefs();
    const excludedColIds = ["edit", "delete"];
    const updatedColumns = excludeColumnsRecursively(columns, excludedColIds);
    const includedColumnKeys = updatedColumns.map((column) => column.colId);
    const params = {
      fileName: name,
      columnKeys: includedColumnKeys,
    };

    let additionalRows = "\n\n";
    if (dynamicTextConditions?.rowData) {
      additionalRows += t("cleaningEffectivnessStrings.note") + "\n";
    }
    if (dynamicTextConditions?.hasInvalidData) {
      additionalRows += t("cleaningEffectivnessStrings.noteWith*") + "\n";
    }
    if (dynamicTextConditions?.hasValuesGreaterThan100) {
      additionalRows +=
        t("cleaningEffectivnessStrings.noteForValuesrGeaterThan100") + "\n";
    }
    if (dynamicTextConditions?.hasValuesLessThan0) {
      additionalRows +=
        t("cleaningEffectivnessStrings.noteForValuesLessThan0") + "\n";
    }
    if (dynamicTextConditions?.hasSuperscripta) {
      additionalRows +=
        t("cleaningEffectivnessStrings.notewithsuperscripta") + "\n";
    }
    if (dynamicTextConditions?.hasSuperscriptb) {
      additionalRows +=
        t("cleaningEffectivnessStrings.notewithsuperscriptb") + "\n";
    }
    // added double quotes to avoid issues with commas in CSV
    if (dynamicTextConditions?.hasFoulingData) {
      additionalRows += '"' + t("foulingStrings.scoreDescription") + '"\n';
    }
    params.customFooter = additionalRows;

    ref.exportDataAsCsv(params);
  }, []);

  const rowClickHandler = (event) => {
    if (!window.isTableCellAction && onRowClicked) {
      onRowClicked(event);
    }
  };

  return (
    <>
      {!hideExport && (
        <div className="flex-between-center">
          <div className="info-section">
            <DupontButton
              type="tertiary"
              onClick={onExportClicked}
              disabled={!rowData || rowData.length === 0}
            >
              {t("ValueStrings.exportCSV")}
            </DupontButton>
            {infolabel && <IconWithTooltip label={infolabel} />}
          </div>
        </div>
      )}
      <div
        className={`${customTableClass}  ${onRowClicked ? "row-pointer" : ""}`}
        style={{ height: customHeight }}
      >
        <AgGridReact
          ref={gridRef || agGridRef}
          domLayout={customHeight === "auto" ? "autoHeight" : "normal"}
          rowData={rowData}
          quickFilterText={quickFilterText}
          onRowClicked={rowClickHandler}
          IdentityCheck="IdentityCheck"
          gridOptions={gridOptions}
          onCellClicked={onCellClicked}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          context={context}
          frameworkComponents={frameworkComponents}
          getRowNodeId={getRowNodeId}
          onRowSelected={onRowSelected}
          suppressClickEdit={false}
          onSelectionChanged={onSelectionChanged}
          onCellValueChanged={onCellValueChanged}
          onGridReady={onAgGridReady}
          onRowDataUpdated={onFilterChanged}
          onFilterChanged={onFilterChanged}
          tooltipShowDelay={500}
          onSortChanged={(event) => {
            if (onSortChanged) onSortChanged(event);
          }}
          suppressScrollOnNewData={suppressScrollOnNewData}
          suppressDragLeaveHidesColumns={true}
          getRowClass={getRowClass}
        />
      </div>
    </>
  );
};

export default AgTable;
